import { motion } from "framer-motion";

/**
 * DestinoMockup — bespoke CSS/SVG animated "screens" that loop, standing in
 * for autoplay-muted-loop product videos. Each mockup shows *interaction*
 * (scroll, reveal, hover) rather than a static screenshot. Coherent per sector.
 */
export default function DestinoMockup({ type, accent = "#FF6B2C" }) {
  if (type === "dental") return <DentalMockup accent={accent} />;
  if (type === "interior") return <InteriorMockup accent={accent} />;
  return <RestaurantMockup accent={accent} />;
}

function Chrome({ children, label }) {
  return (
    <div className="absolute inset-0 flex flex-col" style={{ background: "#FEFCF9" }}>
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-black/5">
        <span className="w-1.5 h-1.5 rounded-full bg-black/15" />
        <span className="w-1.5 h-1.5 rounded-full bg-black/15" />
        <span className="w-1.5 h-1.5 rounded-full bg-black/15" />
        <span className="ml-3 font-sans text-[8px] tracking-[0.2em] uppercase text-black/30">{label}</span>
      </div>
      <div className="relative flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

function RestaurantMockup({ accent }) {
  return (
    <Chrome label="lamichi / restaurante">
      {/* Auto-scrolling editorial page */}
      <motion.div
        animate={{ y: ["0%", "-52%", "0%"] }}
        transition={{ duration: 9, ease: "easeInOut", repeat: Infinity }}
        className="absolute inset-x-0 top-0 px-4 pt-4"
      >
        <div className="font-display text-[7px] tracking-wide text-black/40 mb-1">Menú de temporada</div>
        <div className="font-display leading-[0.9] tightest text-black" style={{ fontSize: "26px" }}>
          Cocina de<br /><span className="italic" style={{ color: accent }}>proximidad.</span>
        </div>
        <div className="mt-3 h-16 rounded-sm bg-black/[0.06] overflow-hidden relative">
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg,#efe7dc,#e3d5c4)" }} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="h-10 rounded-sm bg-black/[0.05]" />
          <div className="h-10 rounded-sm bg-black/[0.05]" />
        </div>
        <div className="mt-3 space-y-1.5">
          <div className="h-1 rounded bg-black/10 w-11/12" />
          <div className="h-1 rounded bg-black/10 w-9/12" />
          <div className="h-1 rounded bg-black/10 w-10/12" />
        </div>
        <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: accent }}>
          <span className="font-sans text-[7px] tracking-wide text-black">Reservar mesa</span>
        </div>
        <div className="mt-4 h-14 rounded-sm bg-black/[0.06]" />
      </motion.div>
      {/* moving cursor */}
      <motion.div
        animate={{ x: [30, 90, 60], y: [90, 40, 120], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 9, ease: "easeInOut", repeat: Infinity }}
        className="absolute w-2 h-2 rounded-full pointer-events-none"
        style={{ background: "#111", mixBlendMode: "difference" }}
      />
    </Chrome>
  );
}

function DentalMockup({ accent }) {
  return (
    <Chrome label="lamichi / clínica">
      <motion.div
        animate={{ y: ["0%", "-48%", "0%"] }}
        transition={{ duration: 10, ease: "easeInOut", repeat: Infinity }}
        className="absolute inset-x-0 top-0 px-4 pt-4"
      >
        <div className="flex items-center justify-between">
          <div className="font-display text-[9px]" style={{ color: "#111" }}>Clínica</div>
          <div className="flex gap-2">
            <span className="h-1 w-4 rounded bg-black/10" />
            <span className="h-1 w-4 rounded bg-black/10" />
          </div>
        </div>
        <div className="mt-4 font-display leading-[0.95] tightest" style={{ fontSize: "22px", color: "#111" }}>
          Tu sonrisa,<br /><span className="italic" style={{ color: accent }}>en calma.</span>
        </div>
        <div className="mt-3 h-20 rounded-md overflow-hidden" style={{ background: "linear-gradient(140deg,#eaf1f5,#d8e6ee)" }} />
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 rounded-sm bg-black/[0.05] flex items-center justify-center">
              <span className="w-3 h-3 rounded-full" style={{ background: accent, opacity: 0.5 }} />
            </div>
          ))}
        </div>
        <motion.div
          animate={{ boxShadow: ["0 0 0 0 rgba(123,166,194,0)", "0 0 0 4px rgba(123,166,194,0.25)", "0 0 0 0 rgba(123,166,194,0)"] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="mt-3 inline-flex px-2.5 py-1 rounded-full"
          style={{ background: accent }}
        >
          <span className="font-sans text-[7px] tracking-wide text-white">Pedir cita</span>
        </motion.div>
        <div className="mt-4 space-y-1.5">
          <div className="h-1 rounded bg-black/10 w-10/12" />
          <div className="h-1 rounded bg-black/10 w-8/12" />
        </div>
      </motion.div>
    </Chrome>
  );
}

function InteriorMockup({ accent }) {
  return (
    <Chrome label="lamichi / interiorismo">
      <motion.div
        animate={{ y: ["0%", "-50%", "0%"] }}
        transition={{ duration: 11, ease: "easeInOut", repeat: Infinity }}
        className="absolute inset-x-0 top-0 px-4 pt-4"
      >
        <div className="font-display text-[7px] tracking-[0.2em] uppercase text-black/40">Portfolio 001 / 012</div>
        <div className="mt-2 font-display leading-[0.9] tightest" style={{ fontSize: "24px", color: "#111" }}>
          Materia<br /><span className="italic">&amp; luz.</span>
        </div>
        <div className="mt-3 h-24 rounded-sm overflow-hidden" style={{ background: "linear-gradient(160deg,#e7e2d8,#cfc7b6)" }} />
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="h-16 rounded-sm bg-black/[0.06]" />
          <div className="h-16 rounded-sm bg-black/[0.04]" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="h-1 flex-1 rounded" style={{ background: accent, opacity: 0.6 }} />
          <span className="font-sans text-[7px] tracking-wide text-black/50">Ver proyecto</span>
        </div>
        <div className="mt-3 h-16 rounded-sm bg-black/[0.05]" />
      </motion.div>
    </Chrome>
  );
}
